import React from 'react';
import { XCircle, X, PlayCircle, Loader } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import ExcelImport from './ExcelImport';

export default function ScrapingTab({
    scrapingForm,
    setScrapingForm,
    scraping,
    scrapeError,
    setScrapeError,
    scrapeResults,
    setScrapeResults,
    onScrape,
    onFetchJobs,
}) {
    const { isDarkMode } = useTheme();

    const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`;
    const labelClass = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;

    return (
        <div className="space-y-6">
            <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Load Batch from Workday Portal</h2>

                {scrapeError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                        <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                            <h3 className="font-medium text-red-900">Scraping Error</h3>
                            <p className="text-sm text-red-700 mt-1">{scrapeError}</p>
                        </div>
                        <button
                            onClick={() => setScrapeError(null)}
                            className="ml-auto p-1 hover:bg-red-100 rounded"
                        >
                            <X size={16} className="text-red-600" />
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className={labelClass}>
                            Portal URL *
                        </label>
                        <input
                            type="url"
                            placeholder="https://company.wd5.myworkdayjobs.com/PortalName"
                            value={scrapingForm.portal_url}
                            onChange={(e) => setScrapingForm(prev => ({ ...prev, portal_url: e.target.value }))}
                            className={inputClass}
                            disabled={scraping}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className={labelClass}>
                                Max Pages
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={scrapingForm.max_pages}
                                onChange={(e) => setScrapingForm(prev => ({ ...prev, max_pages: parseInt(e.target.value) }))}
                                className={inputClass}
                                disabled={scraping}
                            />
                        </div>

                        <div>
                            <label className={labelClass}>
                                Days Back (optional)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="365"
                                value={scrapingForm.days_back}
                                onChange={(e) => setScrapingForm(prev => ({
                                    ...prev,
                                    days_back: e.target.value === '' ? '' : parseInt(e.target.value)
                                }))}
                                className={inputClass}
                                disabled={scraping}
                                placeholder="Leave empty for all jobs"
                            />
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Filter jobs posted in last N days (leave empty to get all jobs)</p>
                        </div>

                        <div>
                            <label className={labelClass}>
                                Search Text (optional)
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., engineer"
                                value={scrapingForm.search_text}
                                onChange={(e) => setScrapingForm(prev => ({ ...prev, search_text: e.target.value }))}
                                className={inputClass}
                                disabled={scraping}
                            />
                        </div>
                    </div>
                </div>

                <button
                    onClick={onScrape}
                    disabled={scraping || !scrapingForm.portal_url}
                    className="w-full mt-4 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {scraping ? (
                        <>
                            <Loader className="animate-spin" size={20} />
                            Scraping in progress...
                        </>
                    ) : (
                        <>
                            <PlayCircle size={20} />
                            Start Scraping
                        </>
                    )}
                </button>
            </div>

            {scrapeResults && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Scraping Results</h3>
                        <button
                            onClick={() => setScrapeResults(null)}
                            className={`text-sm ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                            Clear Results
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                            <div className="text-2xl font-bold text-primary-700">{scrapeResults.new_jobs || 0}</div>
                            <div className="text-sm text-primary-600">New Jobs</div>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-2xl font-bold text-blue-700">{scrapeResults.total_found || 0}</div>
                            <div className="text-sm text-blue-600">Total Found</div>
                        </div>
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <div className="text-2xl font-bold text-red-700">{scrapeResults.errors || 0}</div>
                            <div className="text-sm text-red-600">Errors</div>
                        </div>
                    </div>

                    {scrapeResults.logs && scrapeResults.logs.length > 0 && (
                        <div className="mt-4">
                            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Logs:</h4>
                            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono max-h-64 overflow-y-auto">
                                {scrapeResults.logs.map((log, idx) => (
                                    <div key={idx}>{log}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Excel Import — self-contained, admin mode (saves immediately) */}
            <ExcelImport mode="admin" onSuccess={onFetchJobs} />
        </div>
    );
}